import React, { useCallback, useEffect } from 'react';
import { useSchoolProfile } from '../../hooks/useSchoolProfile';
import { getSchoolThemeStyle } from '../../utils/schoolTheme';
import {
  SITE_ORIGIN,
  resetDocumentMetadata,
  setDocumentMetadata,
} from '../../utils/documentMeta';
import SchoolProfileHero from './SchoolProfileHero';
import SchoolAboutSection from './SchoolAboutSection';
import SchoolMediaGallery from './SchoolMediaGallery';
import SchoolContactSection from './SchoolContactSection';
import SchoolAdmissionSection, { ADMISSION_SECTION_ID } from './SchoolAdmissionSection';
import SchoolProfileFooter from './SchoolProfileFooter';
import SchoolProfileSkeleton from './SchoolProfileSkeleton';
import SchoolProfileNotFound from './SchoolProfileNotFound';
import SchoolProfileError from './SchoolProfileError';
import './schoolProfile.css';

const MAX_META_DESCRIPTION_LENGTH = 160;

/**
 * Absolute, query-free URL for this school page. Always built on the public
 * origin so preview deploys canonicalize to the live page instead of competing
 * with it in search results.
 */
const buildCanonicalUrl = () => {
  if (typeof window === 'undefined') return undefined;
  return `${SITE_ORIGIN}${window.location.pathname}`.replace(/\/$/, '') || SITE_ORIGIN;
};

const buildMetaDescription = ({ schoolName, descriptionParagraphs, address }) => {
  const description = descriptionParagraphs.join(' ').replace(/\s+/g, ' ').trim();

  if (description) {
    if (description.length <= MAX_META_DESCRIPTION_LENGTH) return description;
    return `${description.slice(0, MAX_META_DESCRIPTION_LENGTH - 1).trimEnd()}…`;
  }

  return address
    ? `Learn about ${schoolName} in ${address} — admissions, contact details and photos.`
    : `Learn about ${schoolName} — admissions, contact details and photos.`;
};

/**
 * Public, parent-facing school profile rendered for root-level paths such as
 * /queens-school. Owns the load / not-found / error states and page metadata.
 */
const SchoolProfilePage = ({ publicPath, onApplyForAdmission = null }) => {
  const { profile, isLoading, isNotFound, isError, retry } = useSchoolProfile(publicPath);

  useEffect(() => {
    if (!profile) {
      // Sensible default while the profile is still loading.
      setDocumentMetadata({
        title: 'School Profile | AtadaBoard',
        canonicalUrl: buildCanonicalUrl(),
      });
      return undefined;
    }

    const description = buildMetaDescription(profile);
    const title = `${profile.schoolName} | AtadaBoard`;

    setDocumentMetadata({
      title,
      description,
      ogTitle: profile.schoolName,
      ogDescription: description,
      ogImage: profile.shareImageUrl,
      ogUrl: buildCanonicalUrl(),
      canonicalUrl: buildCanonicalUrl(),
      ogType: 'profile',
    });

    return undefined;
  }, [profile]);

  // Clear metadata only when leaving the page, not on every profile change.
  useEffect(() => resetDocumentMetadata, []);

  const scrollToAdmission = useCallback(() => {
    const section = document.getElementById(ADMISSION_SECTION_ID);
    if (!section) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    section.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  if (isLoading) return <SchoolProfileSkeleton />;
  if (isNotFound) return <SchoolProfileNotFound variant="school" />;
  if (isError || !profile) return <SchoolProfileError onRetry={retry} />;

  return (
    <div className="sp-page" style={getSchoolThemeStyle(profile)}>
      <a className="sp-skip" href="#profile-content">
        Skip to content
      </a>

      <SchoolProfileHero
        profile={profile}
        onApplyClick={onApplyForAdmission || scrollToAdmission}
      />

      <main id="profile-content">
        <SchoolAboutSection profile={profile} />
        <SchoolMediaGallery profile={profile} />
        <SchoolContactSection profile={profile} />
        <SchoolAdmissionSection profile={profile} onApply={onApplyForAdmission} />
      </main>

      <SchoolProfileFooter schoolName={profile.schoolName} />
    </div>
  );
};

export default SchoolProfilePage;
